import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Check, Star, ShieldCheck, Truck, Zap, Award, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackCTAClick } from "@/lib/ga4";

const OptimizedHeroSection = () => {
  const scrollToPricing = () => {
    trackCTAClick("Start My Transformation", "hero_section");
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  const trustPoints = [
    { icon: Award, text: "500mg NAD+" },
    { icon: ShieldCheck, text: "Made in USA" },
    { icon: Zap, text: "30-Day Supply" },
  ];

  return (
    <section className="relative min-h-screen overflow-hidden bg-background pt-16 pb-24 md:pt-20 md:pb-16">
      {/* Premium subtle background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-secondary/50 via-background to-background" />
        <div className="absolute top-1/4 left-1/4 w-[400px] md:w-[600px] h-[400px] md:h-[600px] rounded-full bg-primary/5 blur-[100px] md:blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full bg-accent/5 blur-[80px] md:blur-[120px]" />
      </div>

      <div className="container relative z-10 px-4 mx-auto">
        {/* CREDIBLE URGENCY BAR - Mobile optimized */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 mb-6 md:mb-8 text-xs sm:text-sm"
        >
          <div className="flex items-center gap-1.5 bg-accent/10 border border-accent/20 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full">
            <Gift className="w-3 h-3 sm:w-4 sm:h-4 text-accent" />
            <span className="text-accent font-semibold">Free eBook with 3+ Bottles</span>
          </div>
          <div className="flex items-center gap-1.5 bg-primary/10 border border-primary/20 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full">
            <Truck className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
            <span className="text-primary font-medium">Free Shipping Available</span>
          </div>
        </motion.div>

        {/* MAIN HEADLINE - Mobile optimized */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="text-center mb-4 md:mb-6"
        >
          <div className="inline-block mb-3 md:mb-4">
            <span className="bg-primary/15 border border-primary/30 text-primary px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wider">
              Premium NAD+ Formula
            </span>
          </div>
          <h1 className="font-display text-[26px] leading-[1.1] sm:text-4xl md:text-5xl lg:text-[68px] font-bold md:leading-[1.05] mb-3 md:mb-4 max-w-5xl mx-auto tracking-tight text-foreground px-2">
            Support Cellular Energy &{" "}
            <span className="text-gradient-gold">Healthy Aging</span>
            <br className="hidden xs:block" />
            {" "}— Starting{" "}
            <span className="text-accent">Today</span>
          </h1>
        </motion.div>

        {/* SUBHEADLINE - Mobile optimized */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="text-center mb-5 md:mb-8"
        >
          <p className="text-sm sm:text-base md:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed px-2">
            A premium NAD+ formula designed to support <span className="text-foreground font-semibold">energy, focus, and cellular function</span>.*
          </p>
        </motion.div>

        {/* TRUST POINTS - Mobile optimized (horizontal scroll on small screens) */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="flex justify-center gap-2 md:gap-4 mb-6 md:mb-10 overflow-x-auto pb-2 px-2"
        >
          {trustPoints.map((point, index) => (
            <div 
              key={index} 
              className="flex items-center gap-1.5 md:gap-2 bg-card border border-border px-3 py-2 md:px-4 md:py-2.5 rounded-lg md:rounded-xl shadow-soft flex-shrink-0"
            >
              <point.icon className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0" />
              <span className="text-foreground text-xs md:text-base font-medium whitespace-nowrap">{point.text}</span>
            </div>
          ))}
        </motion.div>

        {/* WORLDWIDE SHIPPING BANNER */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="max-w-2xl mx-auto mb-6 md:mb-8 px-4"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/20 rounded-xl md:rounded-2xl p-3 md:p-4">
            <p className="text-center text-foreground font-semibold text-sm md:text-base mb-2">
              🌍 We Ship Worldwide to Your Country!
            </p>
            <div className="flex flex-wrap justify-center gap-1.5 md:gap-2 text-lg md:text-xl">
              <span title="United States">🇺🇸</span>
              <span title="Canada">🇨🇦</span>
              <span title="Australia">🇦🇺</span>
              <span title="United Kingdom">🇬🇧</span>
              <span title="France">🇫🇷</span>
              <span title="Italy">🇮🇹</span>
              <span title="Netherlands">🇳🇱</span>
              <span title="Belgium">🇧🇪</span>
              <span title="Sweden">🇸🇪</span>
              <span title="Norway">🇳🇴</span>
              <span title="Denmark">🇩🇰</span>
              <span title="Finland">🇫🇮</span>
              <span title="Portugal">🇵🇹</span>
              <span title="New Zealand">🇳🇿</span>
              <span title="South Korea">🇰🇷</span>
              <span title="Thailand">🇹🇭</span>
              <span title="Philippines">🇵🇭</span>
              <span title="Indonesia">🇮🇩</span>
              <span title="Vietnam">🇻🇳</span>
              <span className="text-muted-foreground text-xs md:text-sm self-center">+15 more</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col items-center gap-3 md:gap-5 mb-8 md:mb-12 px-4"
        >
          <Button
            variant="default"
            size="lg"
            onClick={scrollToPricing}
            className="group relative w-full sm:w-auto h-14 md:h-[76px] px-8 md:px-16 text-base md:text-xl font-bold bg-accent text-accent-foreground hover:bg-accent/90 shadow-blue transition-all duration-300 hover:shadow-[0_8px_40px_hsl(221_91%_58%/0.4)] animate-pulse-scale btn-premium rounded-xl md:rounded-2xl"
          >
            <Zap className="w-4 h-4 md:w-5 md:h-5 mr-2" />
            <span>SEE PRICING OPTIONS</span>
            <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
          </Button>
          
          <div className="flex flex-wrap justify-center items-center gap-3 md:gap-6">
            <div className="flex items-center gap-1.5 text-accent font-semibold text-xs md:text-base">
              <Truck className="w-4 h-4 md:w-5 md:h-5" />
              <span>FREE Shipping</span>
            </div>
            <div className="flex items-center gap-1.5 text-primary font-medium text-xs md:text-base">
              <ShieldCheck className="w-4 h-4 md:w-5 md:h-5" />
              <span>60-Day Guarantee</span>
            </div>
          </div>

          {/* FDA Disclaimer - Near first CTA */}
          <p className="text-muted-foreground text-[10px] md:text-xs text-center max-w-md">
            *These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease.
          </p>
        </motion.div>

        {/* Social proof and guarantee */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.7 }}
          className="relative w-full max-w-md md:max-w-lg mx-auto mb-6 md:mb-8 px-2"
        >
          {/* Social proof */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="text-center mb-3 md:mb-5"
          >
            <div className="inline-flex items-center gap-2 md:gap-3 bg-card border border-border rounded-xl md:rounded-2xl px-3 py-2 md:px-5 md:py-3 shadow-soft">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 md:w-5 md:h-5 fill-primary text-primary" />
                ))}
              </div>
              <span className="text-foreground font-bold text-sm md:text-base">4.9/5</span>
              <div className="w-px h-3 md:h-4 bg-border" />
              <span className="text-muted-foreground text-xs md:text-sm">3,420+ Reviews</span>
            </div>
          </motion.div>

          {/* Results guarantee */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 md:gap-3 bg-accent/10 border border-accent/20 px-3 py-2 md:px-5 md:py-3 rounded-lg md:rounded-xl">
              <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-accent flex-shrink-0" />
              <span className="text-foreground font-medium text-xs md:text-base">
                Try Risk-Free with Our <span className="text-accent font-bold">60-Day Money-Back Guarantee</span>
              </span>
            </div>
          </motion.div>
        </motion.div>


        {/* MICRO-TESTIMONIAL - Mobile optimized */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="max-w-xl md:max-w-2xl mx-auto text-center mb-8 md:mb-12 px-4"
        >
          <div className="bg-card border border-border rounded-xl md:rounded-2xl p-4 md:p-6 shadow-soft">
            <p className="text-muted-foreground italic text-sm md:text-lg mb-2 md:mb-3">
              "I've been taking REVIONX for about a month now and I'm really happy with how I feel. More energy throughout the day!"
            </p>
            <div className="flex items-center justify-center gap-1.5 md:gap-2 flex-wrap">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 md:w-4 md:h-4 fill-primary text-primary" />
                ))}
              </div>
              <span className="text-foreground font-semibold text-sm md:text-base">— Sarah M., 52</span>
              <span className="text-accent text-xs font-medium bg-accent/10 px-1.5 py-0.5 md:px-2 rounded-full">Verified</span>
            </div>
          </div>
        </motion.div>

        {/* SCROLL INDICATOR - Hidden on mobile */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="hidden md:flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-2 text-muted-foreground"
          >
            <span className="text-xs uppercase tracking-widest font-medium">Learn more</span>
            <ChevronDown className="w-6 h-6 text-accent" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default OptimizedHeroSection;
